/**
 * @description Component-TradePairPriceInfoMobile
 */
import './index.less';

import { EmptyDataWrap } from '@/components/EmptyDataWrap';
import LeanMoreToolTip from '@/components/ToolTip/LeanMoreToolTip';
import { LEARN_MORE_LINK } from '@/constants/links';
import { WrappedBigNumber } from '@/entities/WrappedBigNumber';
import { WrappedPair } from '@/entities/WrappedPair';
import { useMarketPairInfo, useWatchInstrumentChange } from '@/features/futures/hooks';
import { useFetchFuturesInstrument } from '@/features/futures/query';
import { useWatchMarketListChange } from '@/features/market/hooks';
import { useFetchMarketPairList } from '@/features/market/query';
import { useCurrentPairBaseInfoFromUrl } from '@/features/pair/hook';
import { useChainId } from '@/hooks/web3/useChain';
import FundingRate from '@/pages/components/PairInfo/PairInfoRight/FundingRate';

import classNames from 'classnames';
import { FC, useEffect, useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';

interface IPropTypes {
  currentPair?: WrappedPair;
}

const TradePairPriceInfoMobile: FC<IPropTypes> = ({ currentPair }) => {
  const chainId = useChainId();
  const { t } = useTranslation();
  const currentBaseInfo = useCurrentPairBaseInfoFromUrl(chainId);
  const navigate = useNavigate();

  const marketPairInfo = useMarketPairInfo(chainId, currentBaseInfo?.instrumentAddr, currentBaseInfo?.expiry);

  const markPrice = useMemo(() => {
    return WrappedBigNumber.from(marketPairInfo?.markPrice || currentPair?.wrapAttribute('markPrice') || 0);
  }, [currentPair, marketPairInfo?.markPrice]);

  const openInterestsUsd = useMemo(() => {
    return WrappedBigNumber.from(
      marketPairInfo?.openInterestsUsd || currentPair?.wrapAttribute('openInterestsUSD') || 0,
    );
  }, [currentPair, marketPairInfo?.openInterestsUsd]);

  const fairPrice = useMemo(() => {
    return WrappedBigNumber.from(marketPairInfo?.fairPrice || currentPair?.wrapAttribute('fairPrice') || 0);
  }, [currentPair, marketPairInfo?.fairPrice]);

  const priceChange = useMemo(() => {
    return WrappedBigNumber.from(marketPairInfo?.fairPriceChange24h || 0);
  }, [marketPairInfo?.fairPriceChange24h]);

  const isPriceUp = priceChange.gt(0);
  const isPriceDown = priceChange.lt(0);

  useFetchMarketPairList({ chainId });
  // useWatchMarketListChange(chainId);
  useFetchFuturesInstrument(chainId, currentBaseInfo?.instrumentAddr);
  // useWatchMarketPairInfoChange(chainId, currentBaseInfo?.instrumentAddr, currentBaseInfo?.expiry);
  useWatchMarketListChange(chainId);
  useWatchInstrumentChange(chainId, currentBaseInfo?.instrumentAddr, currentBaseInfo?.expiry);

  // useGetUserProfile();

  useEffect(() => {
    const hash = document.location.hash;
    if (currentPair && !currentPair?.isNormalPair && hash.startsWith('#/earn/')) {
      navigate('/portfolio');
    }
    //  must dep on isNormalPair
  }, [currentPair?.isNormalPair]);

  return (
    <div className="syn-trade-pair-price-info-mobile">
      <div className="syn-trade-pair-price-info-mobile-main">
        <div className="syn-trade-pair-price-info-mobile-main-price">
          <div className="syn-trade-pair-price-info-mobile-main-price-val">
            <EmptyDataWrap isLoading={!fairPrice || fairPrice.eq(0)}>
              {fairPrice?.formatPriceNumberWithTooltip({
                isShowTBMK: true,
              })}
            </EmptyDataWrap>
          </div>
          <div className="syn-trade-pair-price-info-mobile-main-price-change">
            <span
              className={classNames('syn-trade-pair-price-info-mobile-main-price-change-val', {
                up: isPriceUp,
                down: isPriceDown,
              })}>
              {isPriceUp ? '+' : ''}
              {priceChange.formatPercentage({
                hundredfold: false,
                colorShader: false,
              })}
            </span>
            <div className="syn-trade-pair-price-info-mobile-main-price-leverage">
              <span>{currentPair?.maxLeverage}x</span>
            </div>
          </div>
        </div>
        <div className="syn-trade-pair-price-info-mobile-main-info">
          <div className="syn-trade-pair-price-info-mobile-main-info-item">
            <div className="syn-trade-pair-price-info-mobile-main-info-item-label">
              <LeanMoreToolTip title={t('tooltip.tradePage.markPrice')}>{t('common.markP')}</LeanMoreToolTip>
            </div>
            <div className="syn-trade-pair-price-info-mobile-main-info-item-val">
              <EmptyDataWrap isLoading={!markPrice || markPrice.eq(0)}>
                {markPrice?.formatPriceNumberWithTooltip({
                  isShowTBMK: true,
                })}
              </EmptyDataWrap>
            </div>
          </div>
          <div className="syn-trade-pair-price-info-mobile-main-info-item">
            <div className="syn-trade-pair-price-info-mobile-main-info-item-label">{t('common.24HV')}</div>
            <div className="syn-trade-pair-price-info-mobile-main-info-item-val">
              <EmptyDataWrap isLoading={!marketPairInfo?.volume24hUsd}>
                {WrappedBigNumber.from(marketPairInfo?.volume24hUsd || 0).formatNumberWithTooltip({
                  isShowTBMK: true,
                  prefix: '$',
                  isShowApproximatelyEqualTo: false,
                })}
              </EmptyDataWrap>
            </div>
          </div>
          <FundingRate
            chainId={chainId}
            currentPair={currentPair}
            marketPairInfo={marketPairInfo}
            className="syn-trade-pair-price-info-mobile-main-info-item"
          />
          <div className="syn-trade-pair-price-info-mobile-main-info-item">
            <div className="syn-trade-pair-price-info-mobile-main-info-item-label">
              <LeanMoreToolTip link={LEARN_MORE_LINK.oi} title={t('tooltip.tradePage.openInterest')}>
                {t('common.oi')}
              </LeanMoreToolTip>
            </div>
            <div className="syn-trade-pair-price-info-mobile-main-info-item-val">
              <EmptyDataWrap isLoading={!openInterestsUsd}>
                {openInterestsUsd?.formatNumberWithTooltip({
                  isShowTBMK: true,
                  prefix: '$',
                })}
              </EmptyDataWrap>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default TradePairPriceInfoMobile;
